import { useState, useEffect, useCallback, useRef } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import { useMutation, useQuery, useLazyQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";

import { AllstarModalState } from "MVVM/State/modals";
import {
  StorageStatusAtom,
  SavedClipStatusAtom,
} from "MVVM/State/storagePolicy";
import { ModalType } from "MVVM/@types";
import { analytics } from "MVVM/Analytics";
import {
  SAVE_CLIP_MUTATION,
  UNSAVE_CLIP_MUTATION,
} from "MVVM/GraphQL/mutateSavedClips";
import { SAVED_CLIP_STATUS } from "MVVM/GraphQL/savedClipStatus";
import { GET_SAVED_CLIPS } from "MVVM/GraphQL/getSavedClips";
import { useUser } from "MVVM/Hooks/useUser";

import { ESaveClipStep, IStorageStatus, IClipForDeletion } from "./@types";

type SortOption = "LATEST" | "VIEWS" | "BEST";

interface HeaderContent {
  title: string;
  subtitle: string;
  clipName?: string;
}

const getHeaderContent = (
  step: ESaveClipStep,
  storageStatus: IStorageStatus,
  clipTitle: string,
  manageMode: boolean,
): HeaderContent => {
  const baseHeaders: Record<string, HeaderContent> = {
    [ESaveClipStep.CHECKING]: {
      title: "Save Clip",
      subtitle: "Checking storage...",
    },
    [ESaveClipStep.SUMMARY]: {
      title: "Save Clip",
      subtitle: "Keep this clip in your storage permanently",
      clipName: clipTitle,
    },
    [ESaveClipStep.SUCCESS]: {
      title: "Clip Saved",
      subtitle: "Your clip has been saved to your storage",
      clipName: clipTitle,
    },
  };

  if (baseHeaders[step]) {
    return baseHeaders[step];
  }

  if (step === ESaveClipStep.NO_STORAGE) {
    return manageMode
      ? {
          title: "Manage Storage",
          subtitle: `${storageStatus.savedCount}/${storageStatus.limit} clips stored`,
        }
      : {
          title: "Storage Full",
          subtitle: `You've reached your limit of ${storageStatus.limit} saved clips`,
        };
  }

  if (step === ESaveClipStep.DELETE_BROWSE) {
    return manageMode
      ? {
          title: "Manage Storage",
          subtitle:
            "These are all of your saved clips. Unsave them to free up storage.",
        }
      : {
          title: "Storage Full",
          subtitle:
            "Unsave clips to free up space — they'll convert to drafts and expire",
        };
  }

  return { title: "Save Clip", subtitle: "" };
};

export const useSaveClipFlowViewModel = () => {
  const [allstarModalState, setAllstarModalState] =
    useRecoilState(AllstarModalState);
  const setStorageStatusAtom = useSetRecoilState(StorageStatusAtom);
  const setSavedClipStatus = useSetRecoilState(SavedClipStatusAtom);
  const { allstarUser } = useUser();
  const history = useHistory();
  const hasInitialFetch = useRef(false);

  const [step, setStep] = useState<ESaveClipStep>(ESaveClipStep.CHECKING);
  const [storageStatus, setStorageStatus] = useState<IStorageStatus>({
    savedCount: 0,
    limit: 50,
    hasSpace: true,
  });
  const [selectedClipIds, setSelectedClipIds] = useState<string[]>([]);
  const [currentSort, setCurrentSort] = useState<SortOption>("LATEST");
  const [clipsForDeletion, setClipsForDeletion] = useState<IClipForDeletion[]>(
    [],
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isUnlimited, setIsUnlimited] = useState(false);

  const isOpen = allstarModalState.isOpen === ModalType.SaveClipFlow;
  const clipId = allstarModalState.data?.clipId;
  const clipTitle = allstarModalState.data?.clipTitle || "Untitled";
  const clipThumbnail = allstarModalState.data?.clipThumbnail;
  const manageMode = allstarModalState.data?.manageMode === true;

  const updateStorageState = useCallback(
    (savedCount: number, limit: number | null) => {
      const unlimited = limit === null;
      const hasSpace = unlimited || savedCount < limit;
      setStorageStatus({ savedCount, limit: limit ?? 0, hasSpace });
      setStorageStatusAtom({ savedCount, limit, isUnlimited: unlimited });
      return hasSpace;
    },
    [setStorageStatusAtom],
  );

  useQuery(SAVED_CLIP_STATUS, {
    skip: !isOpen,
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (!data?.savedClipStatus) return;

      const { savedCount, limit } = data.savedClipStatus;

      if (limit === null) {
        setStorageStatusAtom({ savedCount, limit: null, isUnlimited: true });
        setIsUnlimited(true);
        if (manageMode) {
          setStorageStatus({ savedCount, limit: 0, hasSpace: true });
          setStep(ESaveClipStep.DELETE_BROWSE);
        } else {
          setStep(ESaveClipStep.CHECKING);
        }
        return;
      }

      setIsUnlimited(false);
      const hasSpace = savedCount < limit;
      setStorageStatus({ savedCount, limit, hasSpace });

      if (manageMode || !hasSpace) {
        setStep(ESaveClipStep.DELETE_BROWSE);
      } else {
        setStep(ESaveClipStep.SUMMARY);
      }
    },
    onError: () => setStep(ESaveClipStep.ERROR),
  });

  const [fetchSavedClips, { loading: savedClipsLoading }] = useLazyQuery(
    GET_SAVED_CLIPS,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        const clips = data?.profile?.clips?.nodes || [];
        setClipsForDeletion(
          clips.map((clip: any) => ({
            id: clip.id,
            internalId: clip.internalId,
            title: clip.title,
            thumbnailUrl: clip.thumbnailUrl,
            views: clip.views,
            createdAt: clip.createdAt,
            saved: clip.saved,
          })),
        );
        if (data?.profile?.savedClipsCount !== undefined) {
          setStorageStatus((prev) => ({
            ...prev,
            savedCount: data.profile.savedClipsCount,
          }));
        }
      },
    },
  );

  const [unsaveClip] = useMutation(UNSAVE_CLIP_MUTATION);

  const [saveClip, { loading: savingClip }] = useMutation(SAVE_CLIP_MUTATION, {
    onCompleted: (data) => {
      if (!data?.saveClip?.success) {
        const errorMessage =
          data?.saveClip?.errors?.[0]?.message || "Failed to save clip";
        setSaveError(errorMessage);
        analytics.track("Save Clip Flow - Error", {
          clipId,
          error: errorMessage,
        });
        return;
      }

      const { savedCount, limit } = data.saveClip;
      updateStorageState(savedCount, limit);
      setStep(ESaveClipStep.SUCCESS);
      setSaveError(null);

      if (clipId) {
        setSavedClipStatus((prev) => ({ ...prev, [clipId]: true }));
      }

      analytics.track("Save Clip Flow - Success", {
        clipId,
        savedCount,
        limit,
      });
    },
    onError: (error) => {
      setSaveError(error.message || "Failed to save clip");
      analytics.track("Save Clip Flow - Error", {
        clipId,
        error: error.message,
      });
    },
  });

  useEffect(() => {
    if (!isOpen) {
      hasInitialFetch.current = false;
      return;
    }

    setSelectedClipIds([]);
    setCurrentSort("LATEST");
    setClipsForDeletion([]);
    setSaveError(null);
    setIsUnlimited(false);
    setStep(ESaveClipStep.CHECKING);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setAllstarModalState({
      data: {},
      functions: {},
      isOpen: ModalType.None,
    });
  }, [setAllstarModalState]);

  const handleSave = useCallback(() => {
    if (!clipId) return;
    saveClip({ variables: { clipId } });
  }, [clipId, saveClip]);

  const fetchClips = useCallback(
    (sort: SortOption) => {
      if (!allstarUser?.user?._id) return;
      fetchSavedClips({
        variables: { userId: allstarUser.user._id, sort, statuses: ["READY"] },
      });
    },
    [allstarUser?.user?._id, fetchSavedClips],
  );

  useEffect(() => {
    if (!isOpen || step !== ESaveClipStep.DELETE_BROWSE) return;
    if (hasInitialFetch.current) return;
    hasInitialFetch.current = true;
    fetchClips(currentSort);
  }, [isOpen, step, fetchClips, currentSort]);

  const handleManageClips = useCallback(() => {
    setStep(ESaveClipStep.DELETE_BROWSE);
    fetchClips(currentSort);
  }, [currentSort, fetchClips]);

  const handleSortChange = useCallback(
    (sort: SortOption) => {
      setCurrentSort(sort);
      fetchClips(sort);
    },
    [fetchClips],
  );

  const handleOpenClip = useCallback((id: string) => {
    window.open(`/clip/${id}`, "_blank");
  }, []);

  const handleSelectClip = useCallback((clipIdToSelect: string) => {
    setSelectedClipIds((prev) =>
      prev.includes(clipIdToSelect)
        ? prev.filter((id) => id !== clipIdToSelect)
        : [...prev, clipIdToSelect],
    );
  }, []);

  const handleRemoveSelected = useCallback(async () => {
    if (selectedClipIds.length === 0) return;

    setIsDeleting(true);

    try {
      const results = await unsaveSelectedClips(
        selectedClipIds,
        clipsForDeletion,
        unsaveClip,
      );

      if (!results.success) return;

      analytics.track("Save Clip Flow - Unsaved Clips", {
        removedCount: results.count,
        source: manageMode ? "manage" : "save_flow",
      });

      const hasSpace = updateStorageState(results.savedCount, results.limit);

      setSavedClipStatus((prev) => {
        const updated = { ...prev };
        results.unsavedIds.forEach((id) => {
          updated[id] = false;
        });
        return updated;
      });
      setSelectedClipIds([]);

      setClipsForDeletion((prev) =>
        prev.filter((clip) => !selectedClipIds.includes(clip.id)),
      );

      if (!manageMode && hasSpace) {
        setStep(ESaveClipStep.SUMMARY);
      }
    } finally {
      setIsDeleting(false);
    }
  }, [
    selectedClipIds,
    clipsForDeletion,
    unsaveClip,
    manageMode,
    updateStorageState,
    setSavedClipStatus,
  ]);

  const handleBackFromDelete = useCallback(() => {
    setSelectedClipIds([]);
    handleClose();
  }, [handleClose]);

  const handleUpgrade = useCallback(() => {
    handleClose();
    history.push("/upgrade");
  }, [handleClose, history]);

  return {
    isOpen,
    step,
    storageStatus,
    clipTitle,
    clipThumbnail,
    manageMode,
    saveError,
    savingClip,
    savedClipsLoading,
    clipsForDeletion,
    selectedClipIds,
    currentSort,
    isDeleting,
    isUnlimited,
    headerContent: getHeaderContent(step, storageStatus, clipTitle, manageMode),
    handleClose,
    handleSave,
    handleManageClips,
    handleSortChange,
    handleOpenClip,
    handleSelectClip,
    handleRemoveSelected,
    handleBackFromDelete,
    handleUpgrade,
  };
};

interface UnsaveResult {
  success: boolean;
  count: number;
  savedCount: number;
  limit: number | null;
  unsavedIds: string[];
}

type UnsaveClipFn = (options: { variables: { clipId: string } }) => Promise<{
  data?: {
    unsaveClip?: { success: boolean; savedCount: number; limit: number | null };
  };
}>;

async function unsaveSelectedClips(
  selectedIds: string[],
  clips: IClipForDeletion[],
  unsaveClipMutation: UnsaveClipFn,
): Promise<UnsaveResult> {
  let successCount = 0;
  let lastResult: { savedCount: number; limit: number | null } | null = null;
  const unsavedIds: string[] = [];

  for (const id of selectedIds) {
    const clip = clips.find((c) => c.id === id);
    if (!clip) continue;

    const result = await unsaveClipMutation({
      variables: { clipId: clip.internalId },
    });

    if (result.data?.unsaveClip?.success) {
      successCount++;
      unsavedIds.push(clip.internalId);
      lastResult = {
        savedCount: result.data.unsaveClip.savedCount,
        limit: result.data.unsaveClip.limit,
      };
    }
  }

  if (successCount === 0 || !lastResult) {
    return {
      success: false,
      count: 0,
      savedCount: 0,
      limit: 0,
      unsavedIds: [],
    };
  }

  return {
    success: true,
    count: successCount,
    savedCount: lastResult.savedCount,
    limit: lastResult.limit,
    unsavedIds,
  };
}
